import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '@redux/hooks';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { setLocale } from '@redux/reducers/locale.reducer';
import { setUserData, setUserDetails } from '@redux/reducers/user.reducer';
import { getSelfWithOrganizationRequest, UserLocale } from '@models/user.model';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import authService from '@services/auth';
import { setTokenLocalStorage, setTokenSessionStorage } from '@config/storage';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

type RegisterSuccess = {
  status: 'SUCCESS';
};

type RegisterFailure = {
  status: 'FAILURE';
  errorMessage?: string;
  statusCode?: string;
};

type RegisterResponse = RegisterSuccess | RegisterFailure;

/**
 * Billing details for a plan whose `requiresPaymentUpfront` is true. Absent for a
 * card-free plan, which is what selects the cheaper `/auth/register` endpoint below.
 */
export type RegisterPaymentParams = {
  billingPeriod: 'MONTHLY' | 'ANNUAL';
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  phone?: string;
  ccNumber: string;
  /** MMYY — the slash the card input shows is stripped by the caller. */
  ccExp: string;
  cvv: string;
};

export type RegisterParams = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  organizationName?: string;
  /** Required whenever organizationName is set; the backend has no default plan. */
  planId?: string;
  payment?: RegisterPaymentParams;
};

type RegisterResult = {
  register: (params: RegisterParams) => Promise<RegisterResponse>;
  isLoading: boolean;
};

export const useRegister = (): RegisterResult => {
  const tActionMessages = useTranslation('actionMessages');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  const register = useCallback(
    async (params: RegisterParams) => {
      setIsLoading(true);
      try {
        // The plan decides the endpoint: only a card-required plan has payment details to
        // send, and only that endpoint charges or vaults a card.
        const data = params.payment
          ? await authService.registerWithPayment({
              firstName: params.firstName,
              lastName: params.lastName,
              email: params.email,
              password: params.password,
              organizationName: params.organizationName as string,
              planId: params.planId as string,
              paymentMethodType: 'credit_card',
              ...params.payment,
            })
          : await authService.register({
              firstName: params.firstName,
              lastName: params.lastName,
              email: params.email,
              password: params.password,
              organizationName: params.organizationName,
              planId: params.planId,
            });

        if (data?.token) {
          // Persist token
          dispatch(setUserData(data.token, true));
          setTokenLocalStorage(data.token);
          setTokenSessionStorage(data.token);

          // Fetch user data with organization
          const userData: getSelfWithOrganizationRequest =
            await graphQlClient.request(
              UserGraphQL.queries.getSelfWithOrganization,
            );

          // Set user details
          dispatch(setUserDetails(userData.getSelfWithOrganization.user));
          dispatch(
            setLocale(
              userData.getSelfWithOrganization.user.locale || UserLocale.EN,
            ),
          );

          // Check if user has an organization
          if (!userData.getSelfWithOrganization.organization) {
            // User has no organization - redirect to no-organization page
            setIsLoading(false);
            toast.success(tActionMessages.registrationSuccessfulWelcome);
            history.push('/no-organization');

            const resp: RegisterResponse = { status: 'SUCCESS' };
            return resp;
          }

          // Set organization data
          dispatch(
            setOrganizationData(userData.getSelfWithOrganization.organization),
          );

          setIsLoading(false);
          toast.success(tActionMessages.registrationSuccessfulWelcome);

          // Redirect to dashboard
          history.push('/');

          const resp: RegisterResponse = { status: 'SUCCESS' };
          return resp;
        }

        // Check if this is a NO_INVITATION_FOUND or ALREADY_INVITED error
        if (data?.statusCode === 'NO_INVITATION_FOUND' || data?.statusCode === 'ALREADY_INVITED') {
          setIsLoading(false);

          // Show toast only for NO_INVITATION_FOUND
          if (data?.statusCode === 'NO_INVITATION_FOUND') {
            toast.error(tActionMessages.noInvitationFoundForEmail);
          }

          const resp: RegisterResponse = {
            status: 'FAILURE',
            errorMessage: data?.errorMessage || tActionMessages.registrationFailed,
            statusCode: data?.statusCode,
          };
          return resp;
        }

        const errorMessage = data?.errorMessage || tActionMessages.registrationFailed;

        // EMAIL_ALREADY_REGISTERED gets an inline card with a sign-in action instead of a
        // toast — a toast that disappears is the wrong medium for a message whose whole
        // point is "here is where to go next".
        if (data?.statusCode !== 'EMAIL_ALREADY_REGISTERED') {
          toast.error(errorMessage);
        }

        setIsLoading(false);
        const resp: RegisterResponse = {
          status: 'FAILURE',
          errorMessage,
          statusCode: data?.statusCode,
        };
        return resp;
      } catch (e: any) {
        const errorMessage =
          e?.response?.data?.errorMessage || e?.message || tActionMessages.registrationFailed;
        toast.error(errorMessage);
        setIsLoading(false);
        const resp: RegisterResponse = { status: 'FAILURE', errorMessage };
        return resp;
      }
    },
    [
      dispatch,
      history,
      tActionMessages.noInvitationFoundForEmail,
      tActionMessages.registrationSuccessfulWelcome,
      tActionMessages.registrationFailed,
    ],
  );

  return { register, isLoading };
};

export default useRegister;
